<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <button @click="openCreateModal" class="btn btn-primary btn-sm">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Course
        </button>
      </template>
    </breadcrumb-index>

    <DataTable
      :items="filteredCourses"
      :columns="columns"
      :loading="loading"
      search-placeholder="Search courses..."
      empty-icon="book-open"
      empty-title="No courses found"
      empty-description="Create your first course to get started"
    >
      <template #filters>
        <select v-model="filterStatus" class="filter-select-modern">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
      </template>

      <template #cell-course="{ item }">
        <div class="table-avatar">
          <img v-if="item.thumbnail" :src="item.thumbnail" class="avatar-img" alt="" />
          <div v-else class="avatar-placeholder">
            <vue-feather type="book" size="18" />
          </div>
          <div class="avatar-info">
            <span class="avatar-name">{{ item.name }}</span>
            <span class="avatar-subtitle">{{ item.code }}</span>
          </div>
        </div>
      </template>

      <template #cell-category="{ item }">
        <span class="badge bg-light text-dark">{{ item.category || '-' }}</span>
      </template>

      <template #cell-lessons="{ item }">
        <span class="fw-medium">{{ item.lessonCount || 0 }}</span>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', getStatusClass(item.status)]">
          {{ item.status }}
        </span>
      </template>

      <template #cell-created="{ item }">
        <span class="text-muted">{{ formatDate(item.createdAt) }}</span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <button 
            class="action-btn action-edit" 
            @click="openEditModal(item)"
            v-tooltip="'Edit course'"
          >
            <vue-feather type="edit-2" size="14" />
          </button>
          <button 
            class="action-btn action-delete" 
            @click="confirmDelete(item)" 
            v-tooltip="{ text: 'Delete course', variant: 'danger' }"
          >
            <vue-feather type="trash-2" size="14" />
          </button>
        </div>
      </template>

      <template #empty-action>
        <button @click="openCreateModal" class="btn btn-primary">
          <vue-feather type="plus" size="16" class="me-1" />
          Create First Course
        </button>
      </template>
    </DataTable>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Course' : 'Add Course'"
      icon="book-open"
      size="lg"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.name || !form.code || !form.category"
      :item-id="isEditing ? editingId : ''"
      @submit="saveCourse"
    >
      <form @submit.prevent="saveCourse" class="modal-form">
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Course Name <span class="text-danger">*</span></label>
            <input 
              v-model="form.name" 
              type="text" 
              class="form-control" 
              placeholder="e.g., Advanced Mathematics"
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label">Course Code <span class="text-danger">*</span></label>
            <input 
              v-model="form.code" 
              type="text" 
              class="form-control" 
              placeholder="e.g., MATH101"
              required
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Category <span class="text-danger">*</span></label>
            <select v-model="form.category" class="form-control" required>
              <option value="">Select Category</option>
              <option value="science">Science</option>
              <option value="mathematics">Mathematics</option>
              <option value="language">Language</option>
              <option value="social">Social Studies</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Status</label>
            <select v-model="form.status" class="form-control">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Description</label>
          <textarea 
            v-model="form.description" 
            class="form-control" 
            rows="3"
            placeholder="Brief description of the course..."
          ></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Thumbnail URL</label>
          <input 
            v-model="form.thumbnail" 
            type="url" 
            class="form-control" 
            placeholder="https://..."
          />
        </div>
      </form>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'

export default {
  name: 'CoursesList',
  components: { DataTable, CreateModal },
  setup() {
    const alert = useAlert()
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Courses', active: true }
    ]

    const columns = [
      { key: 'course', label: 'Course Name', sortable: true, minWidth: '280px' },
      { key: 'category', label: 'Category', sortable: true },
      { key: 'lessons', label: 'Lessons', sortable: true, class: 'text-center' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'created', label: 'Created', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    const courses = ref([])
    const loading = ref(true)
    const filterStatus = ref('')

    // Modal state
    const showModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const form = reactive({
      name: '',
      code: '',
      category: '',
      status: 'draft',
      description: '',
      thumbnail: ''
    })

    const filteredCourses = computed(() => {
      if (!filterStatus.value) return courses.value
      return courses.value.filter(c => c.status === filterStatus.value)
    })

    const fetchCourses = async () => {
      loading.value = true
      try {
        const response = await api.get('/v1/admin/courses')
        courses.value = response.data.courses
      } catch (err) {
        console.error('Failed to fetch courses:', err)
      } finally {
        loading.value = false
      }
    }

    const getStatusClass = (status) => {
      const classes = { 
        active: 'status-active', 
        draft: 'status-inactive', 
        archived: 'status-pending' 
      }
      return classes[status] || 'status-inactive'
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }

    const resetForm = () => {
      form.name = ''
      form.code = ''
      form.category = ''
      form.status = 'draft'
      form.description = ''
      form.thumbnail = ''
      editingId.value = null
      isEditing.value = false
    }

    const openCreateModal = () => {
      resetForm()
      showModal.value = true
    }

    const openEditModal = (course) => {
      isEditing.value = true
      editingId.value = course.id
      form.name = course.name || ''
      form.code = course.code || ''
      form.category = course.category || ''
      form.status = course.status || 'draft'
      form.description = course.description || ''
      form.thumbnail = course.thumbnail || ''
      showModal.value = true
    }

    const saveCourse = async () => {
      if (!form.name || !form.code || !form.category) return
      
      saving.value = true
      try {
        const payload = {
          name: form.name,
          code: form.code,
          category: form.category,
          status: form.status,
          description: form.description,
          thumbnail: form.thumbnail
        }

        if (isEditing.value) {
          const response = await api.put(`/v1/admin/courses/${editingId.value}`, payload)
          const index = courses.value.findIndex(c => c.id === editingId.value)
          if (index !== -1) {
            courses.value[index] = { ...courses.value[index], ...response.data.course || payload }
          }
          alert.success('Updated!', 'Course updated successfully')
        } else {
          const response = await api.post('/v1/admin/courses', payload)
          courses.value.unshift(response.data.course || response.data)
          alert.success('Created!', 'Course created successfully')
        }
        showModal.value = false
        resetForm()
      } catch (error) {
        alert.error('Error', error.response?.data?.error?.message || 'Failed to save course')
      } finally {
        saving.value = false
      }
    }

    const confirmDelete = async (course) => {
      const result = await alert.delete(
        'Delete Course?',
        `Are you sure you want to delete "${course.name}"?`
      )

      if (result.isConfirmed) {
        try {
          await api.delete(`/v1/admin/courses/${course.id}`)
          courses.value = courses.value.filter(c => c.id !== course.id)
          alert.success('Deleted!', 'Course has been deleted.')
        } catch (err) {
          alert.error('Error', 'Failed to delete course')
        }
      }
    }

    onMounted(fetchCourses)

    return { 
      breadcrumbs, 
      columns,
      courses, 
      loading,
      filterStatus, 
      filteredCourses,
      showModal,
      isEditing,
      saving,
      form,
      openCreateModal,
      openEditModal,
      saveCourse,
      getStatusClass, 
      formatDate, 
      confirmDelete 
    }
  }
}
</script>

<style scoped>
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}
</style>
